import { TableAction } from "features/shared/types/actions.table";
import { RowData } from "features/shared/types/table";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { BiFilter } from "react-icons/bi";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import styled from "styled-components";
import { setTimeout } from "timers";

interface Props {
    cellTemplate?: RowData<any>;
    onColumnClickHandler?: () => void;
    children: ReactNode;
}

const HeaderCell: React.FC<Props> = ({
    cellTemplate,
    onColumnClickHandler,
    children,
}) => {
    const ref = useRef<HTMLTableHeaderCellElement>(null);

    const [isDragging, setDragging] = useState<boolean>(false);
    const [isMouseUp, setMouseUp] = useState<boolean>(false);

    const dispatch = useDispatch();
    const { sortOptions, tableHeight } = useSelector((state) => state.table);

    const mouseMove = useCallback(
        (e: MouseEvent) => {
            if (!cellTemplate?.accessor) return;

            // Calculate the column width
            const calculatedWidth =
                e.clientX - (ref.current?.getBoundingClientRect().left || 0);

            if (calculatedWidth >= (cellTemplate.minCellWidth || 100)) {
                dispatch({
                    type: TableAction.SET_COLUMN_HEADER_SIZE,
                    accessor: cellTemplate.accessor,
                    width: calculatedWidth,
                });
            }
        },
        [cellTemplate?.accessor, cellTemplate?.minCellWidth, dispatch]
    );

    const mouseUp = useCallback(() => {
        if (isDragging && cellTemplate?.accessor) {
            dispatch({
                type: TableAction.SET_COLUMN_CONTENT_SIZE,
                accessor: cellTemplate?.accessor,
                width: cellTemplate?.columnWidth,
            });
            setDragging(false);
            setMouseUp(true); // to prevent headerCell accidental onClick
            setTimeout(() => setMouseUp(false), 50);
        }
    }, [
        cellTemplate?.accessor,
        cellTemplate?.columnWidth,
        dispatch,
        isDragging,
    ]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", mouseMove);
            window.addEventListener("mouseup", mouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", mouseMove);
            window.removeEventListener("mouseup", mouseUp);
        };
    }, [isDragging, mouseMove, mouseUp]);

    return (
        <StyledHeaderCell
            onClick={() => {
                if (!isDragging && !isMouseUp && onColumnClickHandler) {
                    onColumnClickHandler();
                }
            }}
            isDragging={isDragging}
            ref={ref}
            isFiltered={sortOptions.sortKey === cellTemplate?.accessor}
        >
            <span>{children}</span>
            {sortOptions.sortKey === cellTemplate?.accessor ? (
                sortOptions.reversed ? (
                    <BiFilter />
                ) : (
                    <BiFilterReverse />
                )
            ) : null}
            {cellTemplate && (
                <ResizeHandle
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={() => setDragging(true)}
                    dragHandleActive={isDragging}
                    height={isDragging ? tableHeight : undefined}
                />
            )}
        </StyledHeaderCell>
    );
};

const BiFilterReverse = styled(BiFilter)`
    transform: rotate(180deg);
`;

const ResizeHandle = styled.div<{
    dragHandleActive: boolean;
    height?: number;
}>`
    display: block;
    position: absolute;
    cursor: col-resize;

    height: ${(props) => `${props.height ? props.height / 16 : 2.5}rem`};
    width: 0.5rem;
    right: -2px;
    top: 0;
    z-index: 1;
    border-right: 2px solid transparent;

    &:hover {
        border-color: ${(props) => props.theme.colors.neutral[200]};
    }

    border-color: ${(props) =>
        props.dragHandleActive
            ? props.theme.colors.neutral[200]
            : "transparent"};
`;

const StyledHeaderCell = styled.th<{
    isFiltered: boolean;
    isDragging: boolean;
}>`
    position: relative;

    display: flex;
    justify-content: center;
    align-items: center;

    height: 2.5rem;

    font-size: 0.9rem;
    font-weight: ${(props) => props.theme.fontWeight.bolder};

    border-right: solid 1px ${(props) => props.theme.colors.neutral[145]};

    background: ${(props) => props.theme.colors.neutral[135]};
    color: #929cb9;

    transition: linear 0.025s;

    &:hover {
        cursor: pointer;
        background: ${(props) =>
            props.isDragging
                ? props.theme.colors.neutral[135]
                : props.theme.colors.neutral[25]};
        color: ${(props) =>
            props.isDragging ? "#929cb9" : props.theme.colors.primary[200]};
    }

    &:last-child {
        border-right: none;
    }

    ${(props) =>
        props.isFiltered
            ? `
        background: white;
        color: ${props.theme.colors.primary[400]};

        
        &:hover {
            cursor: pointer;
            background: ${
                props.isDragging ? "white" : props.theme.colors.neutral[25]
            };
            color: ${
                props.isDragging
                    ? props.theme.colors.primary[400]
                    : props.theme.colors.primary[200]
            };
        }
    `
            : ``};

    &:first-child {
        &:hover {
            color: #929cb9;
        }

        width: 2.5rem;
    }
`;

export default HeaderCell;
